/**
 * Testimonials — Loox-powered, mobile dot carousel preserved.
 *
 * Responsibilities:
 * - Fetch reviews from the Loox Storefront API (sessionStorage TTL cache + inflight dedupe).
 * - Filter (min rating, only-with-media, optional product id) and sort (newest first).
 * - Render each review into the EXACT DOM shape the previous block-based liquid
 *   used so assets/testimonials.css continues to drive the visual design.
 * - Then attach the original mobile-only dot pagination + optional autoplay.
 */

const API_BASE = 'https://storefront-api.loox.io/storefront/v1/store';
const CACHE_PREFIX = 'tt_loox_';
const inflight = new Map();

const BURST_PRESETS = [
  { label: 'POW!',  bg: '#ff3b30', text: '#ffffff' },
  { label: 'BAM!',  bg: '#4ecdc4', text: '#0e0e10' },
  { label: 'ZAP!',  bg: '#ffc83d', text: '#0e0e10' },
  { label: 'BOOM!', bg: '#0e0e10', text: '#ffffff' },
];

const STAR_PATH =
  'M9.84287 1.51122C10.213 0.764273 11.2779 0.764273 11.6481 1.51122L14.0224 6.30205C14.1689 6.59758 14.4504 6.80306 14.7765 6.85194L20.0626 7.64422C20.8865 7.7677 21.2155 8.78014 20.6219 9.36634L16.8127 13.1276C16.5778 13.3596 16.4703 13.6913 16.5249 14.0162L17.4118 19.2885C17.5499 20.1098 16.6884 20.7355 15.9495 20.3502L11.2068 17.8779C10.9143 17.7253 10.5651 17.7253 10.2725 17.8779L5.52986 20.3502C4.79092 20.7355 3.92946 20.1098 4.06762 19.2885L4.95449 14.0162C5.0091 13.6913 4.90163 13.3596 4.66665 13.1276L0.857479 9.36634C0.263867 8.78014 0.59288 7.7677 1.41675 7.64422L6.70289 6.85194C7.02904 6.80306 7.31057 6.59758 7.45702 6.30205L9.84287 1.51122Z';

const CHECKMARK_SVG =
  '<svg xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 56 56" aria-hidden="true" focusable="false">'
  + '<path d="M 23.6641 52.3985 C 26.6172 55.375 29.3594 55.3516 32.3126 52.3985 L 35.9219 48.8125 C 36.2969 48.461 36.625 48.3203 37.1172 48.3203 L 42.1797 48.3203 C 46.3749 48.3203 48.3204 46.3985 48.3204 42.1797 L 48.3204 37.1172 C 48.3204 36.625 48.461 36.2969 48.8124 35.9219 L 52.3749 32.3125 C 55.3749 29.3594 55.3514 26.6172 52.3749 23.6641 L 48.8124 20.0547 C 48.461 19.7031 48.3204 19.3516 48.3204 18.8829 L 48.3204 13.7969 C 48.3204 9.625 46.3985 7.6563 42.1797 7.6563 L 37.1172 7.6563 C 36.625 7.6563 36.2969 7.5391 35.9219 7.1875 L 32.3126 3.6016 C 29.3594 0.625 26.6172 0.6485 23.6641 3.6016 L 20.0547 7.1875 C 19.7032 7.5391 19.3516 7.6563 18.8828 7.6563 L 13.7969 7.6563 C 9.6016 7.6563 7.6563 9.5782 7.6563 13.7969 L 7.6563 18.8829 C 7.6563 19.3516 7.5391 19.7031 7.1876 20.0547 L 3.6016 23.6641 C 0.6251 26.6172 0.6485 29.3594 3.6016 32.3125 L 7.1876 35.9219 C 7.5391 36.2969 7.6563 36.625 7.6563 37.1172 L 7.6563 42.1797 C 7.6563 46.375 9.6016 48.3203 13.7969 48.3203 L 18.8828 48.3203 C 19.3516 48.3203 19.7032 48.461 20.0547 48.8125 Z M 24.0391 39.7891 C 23.3126 39.7891 22.8438 39.5547 22.4923 39.1563 L 14.6641 30.4609 C 14.336 30.086 14.1485 29.6172 14.1485 29.125 C 14.1485 28.0234 14.9923 27.2031 16.1876 27.2031 C 16.8204 27.2031 17.2891 27.4141 17.711 27.8594 L 23.9219 34.7266 L 35.9923 17.7344 C 36.461 17.0547 36.9297 16.7734 37.7501 16.7734 C 38.8985 16.7734 39.7188 17.6172 39.7188 18.7188 C 39.7188 19.1172 39.5547 19.586 39.2969 19.9609 L 25.6328 39.0625 C 25.2813 39.5078 24.7423 39.7891 24.0391 39.7891 Z"/>'
  + '</svg>';

const prefersReducedMotion =
  window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/* ---------- fetch + cache ---------- */
function fetchReviews(storeId, cacheMinutes) {
  const cacheKey = CACHE_PREFIX + storeId;

  try {
    const raw = sessionStorage.getItem(cacheKey);
    if (raw) {
      const cached = JSON.parse(raw);
      if (cached && Date.now() - cached.ts < cacheMinutes * 60 * 1000) {
        return Promise.resolve(cached.data);
      }
    }
  } catch (_) {}

  if (inflight.has(storeId)) return inflight.get(storeId);

  const promise = fetch(`${API_BASE}/${encodeURIComponent(storeId)}/product-reviews`, {
    headers: { Accept: 'application/json' },
    credentials: 'omit',
  })
    .then((response) => {
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      return response.json();
    })
    .then((json) => {
      const reviews = (json && json.reviews) || [];
      try {
        sessionStorage.setItem(cacheKey, JSON.stringify({ ts: Date.now(), data: reviews }));
      } catch (_) {}
      return reviews;
    })
    .finally(() => {
      inflight.delete(storeId);
    });

  inflight.set(storeId, promise);
  return promise;
}

/* ---------- defensive field pickers ---------- */
function pickRating(review) {
  return Number(review.rating || review.stars || review.score || 0);
}

function pickCreated(review) {
  return new Date(review.created_at || review.createdAt || review.date || 0).getTime() || 0;
}

function pickImages(review) {
  const media = review.images || review.photos || review.media || [];
  if (!Array.isArray(media)) return [];
  return media
    .map((item) => {
      if (typeof item === 'string') return item;
      if (!item) return null;
      // Skip videos in the photo slot.
      const url = item.url || item.src || item.thumbnail || item.image_url || '';
      if (item.type === 'video') return null;
      if (typeof url === 'string' && /\.(mp4|webm|mov)(\?|$)/i.test(url)) return null;
      return url || null;
    })
    .filter(Boolean);
}

function pickProductImage(review) {
  if (review.product && (review.product.image || review.product.image_url)) {
    return review.product.image || review.product.image_url;
  }
  return review.product_image || '';
}

function pickProductName(review) {
  return (
    review.product_title ||
    review.product_name ||
    (review.product && (review.product.title || review.product.name)) ||
    ''
  );
}

function pickReviewerName(review) {
  return (
    review.reviewer_name ||
    review.author ||
    review.name ||
    (review.reviewer && review.reviewer.name) ||
    'Verified Buyer'
  );
}

function pickReviewText(review) {
  return review.review || review.body || review.text || review.content || '';
}

function pickVerified(review) {
  return (
    review.verified === true ||
    review.verified_buyer === true ||
    review.is_verified === true
  );
}

/* ---------- filter + sort ---------- */
function applyFilters(reviews, opts) {
  let list = reviews.slice();

  // Default: only with media (matches the existing card design — the photo block is required).
  if (opts.onlyWithMedia) {
    list = list.filter((r) => pickImages(r).length > 0 || pickProductImage(r));
  }
  if (opts.minRating > 0) {
    list = list.filter((r) => pickRating(r) >= opts.minRating);
  }
  if (opts.productId) {
    list = list.filter((r) => {
      const pid = String(r.product_id || r.productId || (r.product && r.product.id) || '');
      return pid === String(opts.productId);
    });
  }

  list.sort((a, b) => pickCreated(b) - pickCreated(a));

  if (opts.limit > 0) list = list.slice(0, opts.limit);
  return list;
}

/* ---------- escape ---------- */
function esc(value) {
  return String(value == null ? '' : value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/* ---------- card renderer (matches sections/testimonials.liquid block markup) ---------- */
function renderStars(rating) {
  const r = Math.max(0, Math.min(5, Math.round(rating || 0)));
  let html = `<div class="testimonial-card__rating" role="img" aria-label="${r} out of 5 stars">`;
  for (let i = 1; i <= 5; i++) {
    html +=
      `<svg class="testimonial-card__star${i <= r ? ' is-filled' : ''}" viewBox="0 0 20 20" aria-hidden="true" focusable="false">` +
      `<path d="${STAR_PATH}"/>` +
      `</svg>`;
  }
  return html + '</div>';
}

function renderPhoto(review) {
  const images = pickImages(review);
  const src = images[0] || pickProductImage(review);
  if (!src) return '<div class="testimonial-card__photo"></div>';
  const alt = esc(pickReviewerName(review));
  return (
    '<div class="testimonial-card__photo">' +
      `<img class="testimonial-card__photo-image" src="${esc(src)}" alt="${alt}" loading="lazy" decoding="async">` +
    '</div>'
  );
}

function renderCard(review, index, opts) {
  const name = pickReviewerName(review);
  const text = pickReviewText(review);
  const rating = pickRating(review);
  const verified = pickVerified(review);

  const verifiedHtml = opts.showVerified && verified
    ? `<span class="testimonial-card__verified" aria-label="Verified buyer">${CHECKMARK_SVG}Verified</span>`
    : '';

  const burst = BURST_PRESETS[index % BURST_PRESETS.length];
  const burstHtml = opts.showBursts
    ? `<span class="testimonial-card__burst" aria-hidden="true" data-burst-text="${esc(burst.label)}">${esc(burst.label)}</span>`
    : '';

  // Match the inline-style pattern the original liquid used per <li>.
  const rotationDir = (index + 1) % 2;
  const rotation = rotationDir === 0
    ? 'calc(var(--testimonials-rotation) * -1)'
    : 'var(--testimonials-rotation)';

  let itemStyle =
    `--testimonials-card-index: ${index + 1};` +
    `--testimonials-card-rotation: ${rotation};`;
  if (opts.showBursts) {
    itemStyle += `--testimonials-burst-bg: ${burst.bg};--testimonials-burst-text: ${burst.text};`;
  }

  const quoteHtml = text
    ? `<div class="testimonial-card__quote"><p>${esc(text)}</p></div>`
    : '';

  return (
    `<li class="testimonials__item" style="${itemStyle}">` +
      '<article class="testimonial-card">' +
        burstHtml +
        renderPhoto(review) +
        '<div class="testimonial-card__content">' +
          '<div class="testimonial-card__head">' +
            `<h3 class="testimonial-card__name">${esc(name)}</h3>` +
            verifiedHtml +
          '</div>' +
          (rating > 0 ? renderStars(rating) : '') +
          quoteHtml +
        '</div>' +
      '</article>' +
    '</li>'
  );
}

/* ---------- options ---------- */
function readOpts(container) {
  return {
    storeId: container.dataset.looxStoreId || '',
    cacheMinutes: parseInt(container.dataset.cacheMinutes, 10) || 5,
    limit: parseInt(container.dataset.limit, 10) || 4,
    minRating: parseInt(container.dataset.minRating, 10) || 0,
    onlyWithMedia: container.dataset.onlyWithMedia === 'true',
    productId: container.dataset.productId || '',
    autoplay: container.dataset.autoplay === 'true',
    autoplaySpeed: parseInt(container.dataset.autoplaySpeed, 10) || 5000,
    showBursts: container.dataset.showBursts !== 'false',
    showVerified: container.dataset.showVerified !== 'false',
    errorMessage: container.dataset.errorMessage || 'Unable to load reviews.',
    emptyMessage: container.dataset.emptyMessage || 'No reviews yet.',
  };
}

/* ---------- mobile dot carousel + optional autoplay ---------- */
class TestimonialsCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-track]');
    this.dotsContainer = this.querySelector('[data-dots]');
    if (!this.track) return;

    this.onScroll = this.onScroll.bind(this);
    this.track.addEventListener('scroll', this.onScroll, { passive: true });

    // Wait for parent section (with data-loox-store-id) to load reviews.
    const section = this.closest('.testimonials');
    if (section) {
      loadReviewsInto(section, this);
    } else {
      this.refreshFromTrack();
    }
  }

  disconnectedCallback() {
    this.track?.removeEventListener('scroll', this.onScroll);
    this.stopAutoplay();
  }

  /** Re-read cards from the track and rebuild dots — called after JS injects cards. */
  refreshFromTrack() {
    if (!this.track) return;
    this.cards = Array.from(this.track.children).filter(
      (el) => !el.classList.contains('testimonials__item--skeleton')
    );
    if (!this.cards.length) return;
    this.buildDots();
    requestAnimationFrame(() => this.syncDot());
  }

  buildDots() {
    if (!this.dotsContainer) return;
    this.dotsContainer.innerHTML = '';
    this.dots = this.cards.map((_card, index) => {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'testimonials__dot';
      dot.setAttribute('aria-label', `Go to testimonial ${index + 1}`);
      if (index === 0) dot.classList.add('is-active');
      dot.addEventListener('click', () => this.scrollToCard(index));
      this.dotsContainer.appendChild(dot);
      return dot;
    });
  }

  scrollToCard(index) {
    const card = this.cards[index];
    if (!card || !this.track) return;
    this.track.scrollTo({
      left: card.offsetLeft - this.track.offsetLeft,
      behavior: prefersReducedMotion ? 'auto' : 'smooth',
    });
  }

  onScroll() {
    if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);
    this.scrollFrame = requestAnimationFrame(() => this.syncDot());
  }

  syncDot() {
    if (!this.track || !this.cards || !this.cards.length || !this.dots) return;
    const first = this.cards[0];
    if (!first) return;
    const cardWidth = first.offsetWidth + 12;
    const index = Math.round(this.track.scrollLeft / cardWidth);
    const clamped = Math.max(0, Math.min(this.dots.length - 1, index));
    this.dots.forEach((dot, i) => dot.classList.toggle('is-active', i === clamped));
  }

  startAutoplay(intervalMs) {
    if (prefersReducedMotion || !this.cards || this.cards.length < 2) return;
    this.stopAutoplay();
    this.autoplayTimer = setInterval(() => {
      if (!this.track || !this.cards.length) return;
      const max = this.track.scrollWidth - this.track.clientWidth;
      if (max <= 0) return;
      // Step by one snap unit: card width + the track's real gap (16px on
      // the mobile carousel, 32px on desktop — a hardcoded value here used
      // to undershoot the snap point and skip the last card). Clamp to the
      // end so the final card is always fully reached, and wrap to the
      // start only after the end position has had its full interval.
      const styles = getComputedStyle(this.track);
      const gap = parseFloat(styles.columnGap || styles.gap) || 0;
      const step = this.cards[0].offsetWidth + gap;
      const atEnd = this.track.scrollLeft >= max - 1;
      const target = atEnd ? 0 : Math.min(this.track.scrollLeft + step, max);
      this.track.scrollTo({ left: target, behavior: 'smooth' });
    }, intervalMs);

    // Bind the pause/resume listeners once — resume re-enters startAutoplay,
    // which previously stacked a fresh set of listeners on every hover.
    if (!this.autoplayListenersBound) {
      this.autoplayListenersBound = true;
      const stop = () => this.stopAutoplay();
      const resume = () => this.startAutoplay(intervalMs);
      this.addEventListener('mouseenter', stop);
      this.addEventListener('mouseleave', resume);
      this.addEventListener('focusin', stop);
      this.addEventListener('focusout', resume);
    }
  }

  stopAutoplay() {
    if (this.autoplayTimer) {
      clearInterval(this.autoplayTimer);
      this.autoplayTimer = null;
    }
  }
}

/* ---------- bootstrap a section ---------- */
function loadReviewsInto(section, carousel) {
  if (section.dataset.tlLooxInit === 'true') return;
  section.dataset.tlLooxInit = 'true';

  const track = carousel.track;
  const errorEl = carousel.querySelector('[data-loox-error]');
  const emptyEl = carousel.querySelector('[data-loox-empty]');
  const opts = readOpts(section);

  function clearStates() {
    if (errorEl) errorEl.hidden = true;
    if (emptyEl) emptyEl.hidden = true;
  }

  function showError() {
    if (track) {
      track.innerHTML = '';
      track.setAttribute('aria-busy', 'false');
    }
    if (errorEl) {
      errorEl.textContent = opts.errorMessage;
      errorEl.hidden = false;
    }
  }

  function showEmpty() {
    if (track) {
      track.innerHTML = '';
      track.setAttribute('aria-busy', 'false');
    }
    if (emptyEl) {
      emptyEl.textContent = opts.emptyMessage;
      emptyEl.hidden = false;
    }
  }

  if (!opts.storeId || !track) {
    showError();
    return;
  }

  fetchReviews(opts.storeId, opts.cacheMinutes)
    .then((all) => {
      clearStates();
      const list = applyFilters(all, opts);
      if (!list.length) {
        showEmpty();
        return;
      }
      track.innerHTML = list.map((review, i) => renderCard(review, i, opts)).join('');
      track.setAttribute('aria-busy', 'false');
      carousel.refreshFromTrack();
      if (opts.autoplay) carousel.startAutoplay(opts.autoplaySpeed);
    })
    .catch((err) => {
      if (window.console && console.warn) {
        console.warn('[testimonials] Loox fetch failed:', err);
      }
      showError();
    });
}

if (!customElements.get('testimonials-carousel')) {
  customElements.define('testimonials-carousel', TestimonialsCarousel);
}

/* Re-init on Shopify editor section reload. */
document.addEventListener('shopify:section:load', (event) => {
  const root = event && event.target;
  if (!root) return;
  const section = root.classList && root.classList.contains('testimonials')
    ? root
    : root.querySelector && root.querySelector('.testimonials');
  if (!section) return;
  const carousel = section.querySelector('testimonials-carousel');
  if (carousel) {
    section.dataset.tlLooxInit = '';
    loadReviewsInto(section, carousel);
  }
});